import React, { useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { getAllUsersWithToken } from '../../api/services/usersService';
import { sendCustomNotification, getNotificationHistory, NotificationLogEntry, SendNotificationResult } from '../../api/services/notificationsService';
import { createCampaign } from '../../api/services/marketingService';
import { useNavigate } from 'react-router-dom';
import { AREA, Avatar, BigButton, Card, FIELD, FieldLabel, LineIcon, LinkButton, Modal, SectionLabel, Segmented, StatusPill } from '../../components/ui/dg';
import dingoLogo from '../../assets/img/logo/dingo-logo.png';

const formatDateTime = (dateString?: string) => {
  if (!dateString) return '—';
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return '—';
  return date.toLocaleString('fr-FR', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
};

interface AppUser {
  id_user: string;
  first_name: string;
  last_name: string;
  email: string;
}

const ManagementNotifications = () => {
  const { token } = useAuth();
  const [users, setUsers] = useState<AppUser[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(true);

  const [target, setTarget] = useState<'all' | 'user'>('all');
  const [userQuery, setUserQuery] = useState('');
  const [selectedUser, setSelectedUser] = useState<AppUser | null>(null);
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState<SendNotificationResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [queued, setQueued] = useState(false);
  const navigate = useNavigate();

  const [history, setHistory] = useState<NotificationLogEntry[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(true);

  const usersById = React.useMemo(() => {
    const map: Record<string, string> = {};
    for (const u of users) map[u.id_user] = `${u.first_name} ${u.last_name}`.trim() || u.email;
    return map;
  }, [users]);

  const loadHistory = () => {
    if (!token) return;
    setLoadingHistory(true);
    getNotificationHistory(token)
      .then(setHistory)
      .catch((err) => console.error('❌ Erreur lors du chargement de l\'historique:', err))
      .finally(() => setLoadingHistory(false));
  };

  useEffect(() => {
    if (!token) return;
    getAllUsersWithToken(token)
      .then((data: any) => setUsers(Array.isArray(data) ? data : []))
      .catch((err) => console.error('❌ Erreur lors du chargement des utilisateurs:', err))
      .finally(() => setLoadingUsers(false));
    loadHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const filteredUsers = userQuery.trim()
    ? users.filter((u) => {
        const needle = userQuery.toLowerCase();
        return (
          `${u.first_name} ${u.last_name}`.toLowerCase().includes(needle) ||
          u.email?.toLowerCase().includes(needle)
        );
      })
    : users.slice(0, 8);

  const canSend = title.trim().length > 0 && body.trim().length > 0 &&
    (target === 'all' || (target === 'user' && !!selectedUser)) && !sending;

  const doSend = async () => {
    if (!token || !canSend) return;
    setSending(true);
    setError(null);
    setResult(null);
    setConfirming(false);
    setQueued(false);
    try {
      // Tous les utilisateurs : une campagne push, à approuver par une autre
      // personne (onglet Campagnes) — l'API refuse l'envoi direct à tous.
      if (target === 'all') {
        await createCampaign(token, {
          kind: 'custom',
          content: {
            fr: { title: title.trim(), body: body.trim(), subject: title.trim(), html: '' },
            ar: { title: '', body: '', subject: '', html: '' },
            en: { title: '', body: '', subject: '', html: '' }
          },
          channels: ['push'],
          audience: 'all',
          link: 'dingo://map',
          note: 'Notification push écrite depuis l’onglet Push.'
        });
        setQueued(true);
        setTitle('');
        setBody('');
        return;
      }
      const res = await sendCustomNotification(
        { title: title.trim(), body: body.trim(), target, userId: selectedUser?.id_user },
        token
      );
      setResult(res);
      loadHistory();
    } catch (err: any) {
      setError(err.message || 'Échec de l\'envoi.');
    } finally {
      setSending(false);
    }
  };

  const handleSendClick = () => {
    setResult(null);
    setError(null);
    // Tout le monde : on confirme d'abord, puis une campagne attend une
    // approbation. Une personne : envoi direct, comme depuis sa fiche.
    if (target === 'all') setConfirming(true);
    else doSend();
  };

  const name = (u: AppUser) => `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.email;

  return (
    <main className="flex-1 overflow-y-auto px-4 pb-6 pt-1 lg:px-7">
      <div className="flex flex-col items-stretch gap-5 xl:flex-row xl:items-start">
        <Card className="flex min-w-0 flex-[1.8] flex-col overflow-hidden lg:flex-row">
          <div className="min-w-0 flex-1 px-7 pb-6 pt-[22px] lg:min-w-[340px]">
            <SectionLabel>Nouveau push</SectionLabel>
            <FieldLabel>Destinataires</FieldLabel>
            <div className="[&>div]:flex [&>div]:w-full [&_button]:flex-1">
              <Segmented ariaLabel="Destinataires" value={target} onChange={(id) => setTarget(id as 'all' | 'user')}
                options={[{ id: 'all', label: 'Tout le monde' }, { id: 'user', label: 'Une personne' }]} />
            </div>
            {target === 'user' && (
              selectedUser ? (
                <div className={`${FIELD} mt-3 flex items-center gap-3`}>
                  <Avatar name={name(selectedUser)} size={30} />
                  <b className="min-w-0 flex-1 truncate text-[14px]">{name(selectedUser)}</b>
                  <LinkButton onClick={() => setSelectedUser(null)}>Changer</LinkButton>
                </div>
              ) : (
                <div className="relative mt-3">
                  <input value={userQuery} onChange={(e) => setUserQuery(e.target.value)} placeholder={loadingUsers ? 'Chargement des comptes…' : 'Chercher par nom ou e-mail…'} className={FIELD} />
                  {userQuery.trim() && (
                    <div className="absolute left-0 right-0 top-full z-10 mt-1.5 max-h-64 overflow-y-auto rounded-2xl border border-[#EEF0F4] bg-white py-1.5 shadow-[0_10px_30px_rgba(15,23,42,0.15)]">
                      {filteredUsers.length === 0 ? <p className="px-4 py-2 text-[13px] text-[#6B7280]">Aucun compte trouvé.</p> : filteredUsers.slice(0, 8).map((u) => (
                        <button key={u.id_user} type="button" onClick={() => { setSelectedUser(u); setUserQuery(''); }} className="flex w-full items-center gap-3 px-4 py-2 text-left hover:bg-[#F7F7FB]">
                          <Avatar name={name(u)} size={30} />
                          <span className="min-w-0"><b className="block truncate text-[13.5px]">{name(u)}</b><span className="block truncate text-[12px] text-[#6B7280]">{u.email}</span></span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )
            )}
            <FieldLabel htmlFor="push-title">Titre</FieldLabel>
            <input id="push-title" value={title} maxLength={80} onChange={(e) => setTitle(e.target.value)} placeholder="Ex. Nouveau code promo disponible" className={`${FIELD} font-bold`} />
            <FieldLabel htmlFor="push-body">Message</FieldLabel>
            <textarea id="push-body" value={body} rows={4} maxLength={240} onChange={(e) => setBody(e.target.value)} placeholder="Le texte affiché dans la notification…" className={AREA} />
            {target === 'all' && (
              <p className="mt-3.5 rounded-2xl bg-[#F7F7FB] px-4 py-3 text-[13px] leading-[1.5] text-[#4B5563]">
                Pour tout le monde, le push devient une campagne. Une autre personne du staff la relit avant l’envoi.
              </p>
            )}
            {error && <p className="mt-3.5 rounded-xl bg-[#FDECEF] px-3 py-2 text-[13px] text-[#B91C1C]">{error}</p>}
            {result && (
              <p className={`mt-3.5 rounded-xl px-3 py-2 text-[13px] ${result.failed ? 'bg-[#FFF8EB] text-[#92400E]' : 'bg-[#ECFDF5] text-[#047857]'}`}>
                {result.message}{typeof result.totalTokens === 'number' && result.totalTokens > 0 ? `, ${result.sent}/${result.totalTokens} appareils atteints` : ''}
              </p>
            )}
            {queued && (
              <p className="mt-3.5 flex flex-wrap items-center justify-between gap-2 rounded-xl bg-[#ECFDF5] px-3 py-2 text-[13px] text-[#047857]">
                Campagne créée. Une autre personne doit l’approuver.
                <LinkButton onClick={() => navigate('/communication')}>Ouvrir dans Campagnes</LinkButton>
              </p>
            )}
          </div>

          {/* Le push tel qu'il s'affichera */}
          <div className="flex flex-col items-center border-t border-[#EEF0F4] bg-[#FAFAFD] px-6 py-7 lg:w-[330px] lg:shrink-0 lg:border-l lg:border-t-0">
            <div className="w-[260px] rounded-[34px] bg-[#1E293B] p-[11px]">
              <div className="flex h-[420px] flex-col items-center rounded-[26px] bg-gradient-to-b from-[#6C63F0] to-[#4F46E5] px-2.5 pt-[38px]">
                <span className="text-[44px] font-light tracking-[-1px] text-white">09:41</span>
                <div className="mt-[22px] flex w-full gap-[9px] rounded-2xl bg-white/95 p-[11px] shadow-[0_6px_20px_rgba(15,23,42,0.18)]">
                  <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-white shadow-[0_0_0_1px_#EEF0F4]"><img src={dingoLogo} alt="" className="w-6" /></span>
                  <span className="min-w-0 flex-1">
                    <span className="flex justify-between text-[11px]"><b>Dingo</b><span className="text-[#6B7280]">maintenant</span></span>
                    <b className="block text-[12.5px] leading-4 text-[#1E293B]">{title || 'Titre du push'}</b>
                    <span className="mt-0.5 block text-[12px] leading-4 text-[#1E293B] line-clamp-4">{body || 'Le message'}</span>
                  </span>
                </div>
              </div>
            </div>
            <BigButton onClick={handleSendClick} disabled={!canSend} className="mt-6 w-full">
              {sending ? (target === 'all' ? 'Création…' : 'Envoi…') : target === 'all' ? 'Soumettre pour approbation' : 'Envoyer'}
            </BigButton>
          </div>
        </Card>

        <Card className="min-w-0 flex-1 px-7 pb-3 pt-[22px] xl:min-w-[320px]">
          <SectionLabel>Historique</SectionLabel>
          {loadingHistory ? (
            <div className="mt-3 space-y-2">{[0, 1, 2, 3].map((i) => <div key={i} className="h-12 animate-pulse rounded-xl bg-[#EEF0F5]" />)}</div>
          ) : history.length === 0 ? (
            <p className="py-8 text-center text-[13px] text-[#6B7280]">Aucun envoi pour l’instant.</p>
          ) : (
            <ul className="mt-1.5 max-h-[70vh] overflow-y-auto">
              {history.map((log, i) => (
                <li key={log.id_log} className={`flex items-center gap-3.5 py-[13px] ${i ? 'border-t border-[#EEF0F4]' : ''}`}>
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#EFEEFD]">
                    <LineIcon d="M6 16V11a6 6 0 0 1 12 0v5l2 2H4zM10 20a2 2 0 0 0 4 0" size={17} color="#4F46E5" />
                  </span>
                  <span className="min-w-0 flex-1">
                    <b className="block truncate text-[14px] font-bold text-[#1E293B]">{log.subject}</b>
                    <span className="block truncate text-[12.5px] text-[#6B7280]">
                      {log.recipient === 'all' ? 'Tout le monde' : usersById[log.recipient] || `Compte ${log.recipient.slice(0, 8)}`} · {formatDateTime(log.created_at)}
                    </span>
                    {log.error && <span className="block truncate text-[12px] text-[#B91C1C]">{log.error}</span>}
                  </span>
                  <StatusPill tone={log.status === 'sent' ? 'green' : 'red'}>{log.status === 'sent' ? 'Envoyé' : 'Échec'}</StatusPill>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>

      {confirming && (
        <Modal title="Proposer à tout le monde ?" onClose={() => setConfirming(false)} width={480}>
          <p className="mt-2 text-[14px] leading-[1.55] text-[#4B5563]">
            {users.length ? `${users.length.toLocaleString('fr-FR')} comptes` : 'Tous les comptes'} recevront ce push une fois la campagne approuvée par une autre personne du staff. Un envoi ne se rattrape pas.
          </p>
          <div className="mt-4 rounded-2xl bg-[#F7F7FB] px-4 py-3.5">
            <b className="block text-[14px] font-bold text-[#1E293B]">{title}</b>
            <span className="mt-0.5 block text-[12.5px] text-[#6B7280] line-clamp-2">{body}</span>
          </div>
          <div className="mt-6 flex gap-3">
            <BigButton kind="secondary" onClick={() => setConfirming(false)} className="flex-1">Annuler</BigButton>
            <BigButton onClick={doSend} disabled={sending} className="flex-1">Soumettre</BigButton>
          </div>
        </Modal>
      )}
    </main>
  );
};

export default ManagementNotifications;
